import http from "./http";

function normalizeUser(user) {
  return {
    id: String(user?.id ?? user?.email ?? user?.name ?? "guest"),
    name: user?.name || user?.email?.split("@")[0] || "User",
    email: user?.email || "",
    role: String(user?.role || "user").toLowerCase(),
  };
}

function normalizeItem(item = {}) {
  return {
    id: item.id,
    folderId: item.folder_id,
    userId: item.user_id,
    type: item.type,
    name: item.name,
    size: item.size || 0,
    mimeType: item.mime_type || "",
    textContent: item.text_content || "",
    url: item.url || "",
    fileAssetId: item.file_asset_id || "",
    createdAt: item.created_at,
    updatedAt: item.updated_at,
    asset: item.asset || null,
  };
}

function normalizeFolder(folder = {}) {
  return {
    id: folder.id,
    ownerId: folder.user_id,
    ownerName: folder.owner_name || "Unknown user",
    ownerEmail: folder.owner_email || "",
    ownerRole: folder.owner_role || "user",
    name: folder.name,
    isStarred: Boolean(folder.is_starred),
    deletedAt: folder.deleted_at || null,
    createdAt: folder.created_at,
    updatedAt: folder.updated_at,
    items: Array.isArray(folder.items) ? folder.items.map(normalizeItem) : [],
  };
}

function triggerBlobDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename || "download";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
}

class WorkspaceService {
  normalizeUser(user) {
    return normalizeUser(user);
  }

  async getFolders(options = {}) {
    const { admin = false, ownerId, search } = options;
    const endpoint = admin ? "/admin/workspaces/folders" : "/workspaces/folders";
    const params = {};

    if (ownerId && admin) {
      params.owner_id = ownerId;
    }
    if (search && admin) {
      params.search = search;
    }

    const response = await http.get(endpoint, { params });
    return Array.isArray(response.data) ? response.data.map(normalizeFolder) : [];
  }

  async createFolder(_owner, name) {
    const response = await http.post("/workspaces/folders", { name });
    return normalizeFolder(response.data);
  }

  async renameFolder(folderId, name) {
    const response = await http.patch(`/workspaces/folders/${folderId}`, { name });
    return normalizeFolder(response.data);
  }

  async toggleFolderStar(folderId, nextValue) {
    const response = await http.patch(`/workspaces/folders/${folderId}`, {
      is_starred: nextValue,
    });
    return normalizeFolder(response.data);
  }

  async moveFolderToTrash(folderId) {
    await http.post(`/workspaces/folders/${folderId}/trash`);
    return { ok: true };
  }

  async restoreFolder(folderId) {
    await http.post(`/workspaces/folders/${folderId}/restore`);
    return { ok: true };
  }

  async deleteFolderPermanently(folderId) {
    await http.delete(`/workspaces/folders/${folderId}`);
    return { ok: true };
  }

  async uploadFileItem(folderId, type, file) {
    const formData = new FormData();
    formData.append("item_type", type);
    formData.append("file", file);

    const response = await http.post(`/workspaces/folders/${folderId}/items/upload`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return normalizeItem(response.data);
  }

  async addTextItem(folderId, payload) {
    const response = await http.post(`/workspaces/folders/${folderId}/items/text`, {
      name: payload.name,
      text_content: payload.textContent,
    });
    return normalizeItem(response.data);
  }

  async addLinkItem(folderId, payload) {
    const response = await http.post(`/workspaces/folders/${folderId}/items/link`, {
      name: payload.name,
      url: payload.url,
    });
    return normalizeItem(response.data);
  }

  async removeItem(itemId) {
    await http.delete(`/workspaces/items/${itemId}`);
    return { ok: true };
  }

  async downloadItem(item) {
    if (item.url) {
      window.open(item.url, "_blank", "noopener,noreferrer");
      return;
    }

    if (item.type === "text") {
      const blob = new Blob([item.textContent || ""], { type: "text/plain;charset=utf-8" });
      triggerBlobDownload(blob, `${item.name || "note"}.txt`);
      return;
    }

    if (!item.fileAssetId) {
      return;
    }

    const response = await http.get(`/files/${item.fileAssetId}`, {
      responseType: "blob",
    });
    triggerBlobDownload(response.data, item.name);
  }
}

export default new WorkspaceService();
